import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

const About = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.getSiteData().then(response => {
      setContent(response.data[language].about);
    }).catch(err => console.error('Error loading about data:', err));
  }, [language]);

  if (!content) return <div className="py-32 bg-[#0a0a0b]" />;

  return (
    <section id="about" className="relative py-32 bg-gradient-to-b from-[#0a0a0b] to-[#0f0f10] overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-violet-600/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6"
            >
              <span className="text-sm text-cyan-400 font-medium font-mono">
                {language === 'fr' ? 'À PROPOS' : 'ABOUT ME'}
              </span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-balance hyphens-none">
              {content.title}
            </h2>
            <p className="text-xl text-gray-400 leading-relaxed mb-8 text-pretty">
              {content.description}
            </p>

            {/* Skills */}
            <div className="space-y-6">
              {content.skills.map((skill, index) => (
                <motion.div
                  key={skill.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">{skill.name}</span>
                    <span className="text-cyan-400 text-sm font-mono">{skill.level}%</span>
                  </div>
                  <div className="h-2 bg-[#1a1a1b] rounded-full overflow-hidden border border-cyan-500/10">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: index * 0.1 + 0.3, ease: 'easeOut' }}
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right side - Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Stats cards */}
            <div className="grid grid-cols-1 gap-6">
              {content.stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="group relative p-8 bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-2xl border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-500 overflow-hidden"
                >
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(0, 217, 255, 0.05), transparent 70%)'
                    }}
                  />
                  <div className="relative z-10">
                    <div className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-400 text-lg">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Decorative element */}
            <motion.div
              animate={{
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear'
              }}
              className="absolute -top-20 -right-20 w-40 h-40 border border-cyan-500/10 rounded-full"
            />
            <motion.div
              animate={{
                rotate: [360, 0],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: 'linear'
              }}
              className="absolute -bottom-10 -left-10 w-32 h-32 border border-blue-500/10 rounded-full"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
